import type { QuoteCalculation, FieldData } from '../types';

interface QuoteResultsProps {
  calculation: QuoteCalculation;
  fieldData: FieldData;
  onStartOver: () => void;
}

export default function QuoteResults({ calculation, fieldData, onStartOver }: QuoteResultsProps) {
  const { pricing, complexity, inputs, estimatedDuration } = calculation;

  const applicationTypeLabels: Record<string, string> = {
    'herbicide': 'Herbicide',
    'fungicide': 'Fungicide',
    'insecticide': 'Insecticide',
    'foliar-nutrition': 'Foliar Nutrition',
    'pgr': 'Plant Growth Regulators',
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Calculate estimated monthly payment (assuming 12-month financing at ~8% APR)
  const estimatedMonthlyPayment = Math.round((pricing.finalTotal * 1.04) / 12 * 100) / 100;

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Main Quote Card */}
      <div className="bg-white rounded-lg border-2 border-emerald-600 p-8 shadow-lg">
        <div className="text-center mb-8">
          <div className="inline-block bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-bold mb-4">
            Your Instant Quote
          </div>
          <div className="text-5xl font-bold text-emerald-800 mb-2">
            ${pricing.finalTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
          <p className="text-gray-600">
            ${pricing.finalRate.toFixed(2)}/acre × {fieldData.acreage} acres
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Estimated application time: {estimatedDuration}
          </p>
          
          {/* Financing Option */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-1">Financing Available</p>
            <p className="text-2xl font-bold text-gray-900">
              ${estimatedMonthlyPayment.toFixed(2)}<span className="text-base font-normal text-gray-600">/month</span>
            </p>
            <p className="text-xs text-gray-500">Estimated payment with approved credit</p>
          </div>
        </div>

        {/* Service Summary */}
        <div className="grid md:grid-cols-3 gap-6 mb-8 pb-8 border-b border-gray-200">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{fieldData.acreage}</div>
            <p className="text-sm text-gray-600">Acres</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{inputs.numberOfApplications}</div>
            <p className="text-sm text-gray-600">Application{inputs.numberOfApplications !== 1 ? 's' : ''}</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{applicationTypeLabels[inputs.applicationType]}</div>
            <p className="text-sm text-gray-600">Application Type</p>
          </div>
        </div>

        {/* Referral Incentive */}
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-lg p-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <i className="ri-gift-line text-2xl text-amber-600"></i>
            <h3 className="text-xl font-bold text-amber-900">Referral Bonus</h3>
          </div>
          <p className="text-amber-900 font-medium">
            Send a referral, get <span className="text-2xl font-bold">10% off</span> your next application
          </p>
          <p className="text-sm text-amber-800 mt-2">
            Know another farmer who could benefit? We'll reward you both.
          </p>
        </div>
      </div>

      {/* Pricing Breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <i className="ri-file-list-line text-emerald-700"></i>
          Pricing Breakdown
        </h3>

        <div className="space-y-4">
          <div className="flex justify-between items-center pb-3 border-b border-gray-100">
            <div>
              <p className="font-medium text-gray-900">Base Rate</p>
              <p className="text-sm text-gray-500">Standard rate for flat, simple fields</p>
            </div>
            <span className="font-bold text-gray-900">${pricing.baseRate.toFixed(2)}/acre</span>
          </div>

          {pricing.complexityAdjustedRate > pricing.baseRate && (
            <div className="flex justify-between items-center pb-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Complexity Adjustment</p>
                <p className="text-sm text-gray-500">
                  Based on field shape, terrain, and obstacles
                </p>
              </div>
              <span className="font-bold text-orange-600">
                +${(pricing.complexityAdjustedRate - pricing.baseRate).toFixed(2)}/acre
              </span>
            </div>
          )}

          {pricing.seasonalRate > pricing.complexityAdjustedRate && (
            <div className="flex justify-between items-center pb-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Seasonal Demand</p>
                <p className="text-sm text-gray-500">
                  Peak season ({monthNames[inputs.applicationMonth - 1]})
                </p>
              </div>
              <span className="font-bold text-orange-600">
                +${(pricing.seasonalRate - pricing.complexityAdjustedRate).toFixed(2)}/acre
              </span>
            </div>
          )}

          {pricing.volumeDiscountRate < pricing.seasonalRate && (
            <div className="flex justify-between items-center pb-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Volume Discount</p>
                <p className="text-sm text-gray-500">
                  Large acreage tier pricing
                </p>
              </div>
              <span className="font-bold text-emerald-600">
                -${(pricing.seasonalRate - pricing.volumeDiscountRate).toFixed(2)}/acre
              </span>
            </div>
          )}

          <div className="flex justify-between items-center pb-3 border-b border-gray-200">
            <div>
              <p className="font-medium text-gray-900">Subtotal</p>
              <p className="text-sm text-gray-500">
                {fieldData.acreage} acres × ${pricing.finalRate.toFixed(2)}/acre
              </p>
            </div>
            <span className="font-bold text-gray-900">
              ${pricing.subtotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>

          {pricing.twoAppDiscount > 0 && (
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <div>
                <p className="font-medium text-gray-900">2-Application Commitment</p>
                <p className="text-sm text-gray-500">10% discount applied</p>
              </div>
              <span className="font-bold text-emerald-600">
                -${pricing.twoAppDiscount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
          )}

          {pricing.totalBeforeMinimum < pricing.minimumCharge && (
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <div>
                <p className="font-medium text-gray-900">Minimum Charge</p>
                <p className="text-sm text-gray-500">75-acre minimum billing</p>
              </div>
              <span className="font-bold text-gray-900">
                ${pricing.minimumCharge.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center pt-4">
            <p className="text-xl font-bold text-gray-900">Total</p>
            <span className="text-3xl font-bold text-emerald-800">
              ${pricing.finalTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </span>
          </div>
        </div>
      </div>

      {/* Complexity Score */}
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <i className="ri-bar-chart-line text-emerald-700"></i>
          Field Complexity Analysis
        </h3>

        <div className="space-y-6">
          {/* Shape Complexity */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Field Shape</span>
              <span className="text-sm font-bold text-gray-900">
                {(complexity.shapeComplexity * 100).toFixed(0)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-emerald-600 h-2 rounded-full transition-all"
                style={{ width: `${complexity.shapeComplexity * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {complexity.shapeComplexity < 0.3 ? 'Simple, box-shaped field' :
               complexity.shapeComplexity < 0.6 ? 'Moderate irregularity' :
               'Complex shape with many turns'}
            </p>
          </div>

          {/* Tree Overhang */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Tree Overhang / Obstacles</span>
              <span className="text-sm font-bold text-gray-900">
                {(complexity.treeOverhangFactor * 100).toFixed(0)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full transition-all"
                style={{ width: `${complexity.treeOverhangFactor * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {inputs.treeOverhang === 'none' ? 'Open field, no obstacles' :
               inputs.treeOverhang === 'light' ? 'Few trees on edges' :
               inputs.treeOverhang === 'moderate' ? 'Tree lines on 1-2 sides' :
               'Surrounded by trees'}
            </p>
          </div>

          {/* Slope */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Terrain / Slope</span>
              <span className="text-sm font-bold text-gray-900">
                {(complexity.slopeFactor * 100).toFixed(0)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-amber-500 h-2 rounded-full transition-all"
                style={{ width: `${complexity.slopeFactor * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {inputs.hillsSlope === 'flat' ? 'Flat terrain' :
               inputs.hillsSlope === 'gentle' ? 'Slight rolling hills' :
               inputs.hillsSlope === 'moderate' ? 'Noticeable slopes' :
               'Significant elevation changes'}
            </p>
          </div>

          {/* Field Spacing */}
          {complexity.spacingFactor > 0 && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Field Spacing / Travel</span>
                <span className="text-sm font-bold text-gray-900">
                  {(complexity.spacingFactor * 100).toFixed(0)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${complexity.spacingFactor * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Multiple fields, {inputs.fieldSpacing} miles apart
              </p>
            </div>
          )}

          {/* Overall Complexity */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold text-gray-900">Overall Complexity</span>
              <span className="text-lg font-bold text-emerald-800">
                {(complexity.overallComplexity * 100).toFixed(0)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-emerald-500 to-orange-500 h-3 rounded-full transition-all"
                style={{ width: `${complexity.overallComplexity * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Battery Changes */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Estimated Battery Changes</p>
                <p className="text-sm text-gray-500">Based on field size and coverage rate</p>
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {complexity.batteryChangesEstimate}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <i className="ri-alert-line text-amber-600 text-xl mt-0.5"></i>
          <div className="text-sm text-amber-900">
            <p className="font-bold mb-2">Important Disclaimer</p>
            <p className="mb-2">
              <strong>This is an estimate for planning purposes.</strong> Final pricing requires field verification and product requirements confirmation.
            </p>
            <ul className="space-y-1 text-amber-800">
              <li>• Actual acreage may differ from satellite measurements</li>
              <li>• Product costs are not included (customer-supplied or quoted separately)</li>
              <li>• Weather conditions may affect scheduling and application timing</li>
              <li>• Wind risk estimates are based on historical climatology data</li>
              <li>• Complexity factors are estimates based on your input</li>
              <li>• Financing subject to credit approval and terms</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Financing Information */}
      <div className="bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center shrink-0 bg-emerald-800 rounded-lg">
            <i className="ri-bank-card-line text-white text-2xl"></i>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Flexible Financing Available
            </h3>
            <div className="space-y-3 text-gray-700">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-check-line text-emerald-700 text-lg"></i>
                </div>
                <p className="text-sm">
                  <strong className="text-gray-900">0% APR promotional plans available</strong> — Ask us about current offers
                </p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-check-line text-emerald-700 text-lg"></i>
                </div>
                <p className="text-sm">
                  Payment terms from 6 to 24 months
                </p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-check-line text-emerald-700 text-lg"></i>
                </div>
                <p className="text-sm">
                  Quick approval process — most decisions within 24 hours
                </p>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-white/80 rounded-lg border border-emerald-200">
              <p className="text-sm text-gray-800 mb-3">
                <strong className="text-emerald-900">Special Offer:</strong> Ask about our 0% APR promotional financing plans when you contact us for your quote.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="tel:+1234567890"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-800 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line"></i>
                  Call for Financing Details
                </a>
                <a
                  href="/instant-quote#contact"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-emerald-800 font-semibold rounded-lg border-2 border-emerald-800 hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-message-3-line"></i>
                  Request Information
                </a>
              </div>
            </div>

            <p className="text-xs text-gray-600 mt-4 leading-relaxed">
              <i className="ri-information-line mr-1"></i>
              Financing subject to credit approval and qualification requirements. 0% APR promotional plans available for qualified applicants on approved terms. Contact us for complete details, current promotional offers, and qualification criteria.
            </p>
          </div>
        </div>
      </div>

      {/* Estimated Monthly Payment (Standard Financing) */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Estimated Monthly Payment
          </span>
          <span className="text-xs text-gray-500">(Standard 12-month financing)</span>
        </div>
        <div className="text-3xl font-bold text-gray-900">
          ${estimatedMonthlyPayment.toFixed(2)}
          <span className="text-lg text-gray-600 font-normal">/month</span>
        </div>
        <p className="text-xs text-gray-600 mt-2">
          Based on 12-month term at ~8% APR. Ask about 0% APR promotional options to reduce or eliminate interest charges.
        </p>
      </div>

      {/* Value Insight Panel */}
      <div className="bg-stone-50 border border-stone-200 rounded-lg px-8 py-7">
        <p className="text-stone-700 text-lg italic leading-relaxed">
          &ldquo;Sometimes you get what you pay for.
          <br />
          And sometimes you pay twice.&rdquo;
        </p>
        <p className="text-stone-500 text-sm mt-4 leading-relaxed max-w-2xl">
          Not all drone applications are created equal. Coverage, timing, drift control, and follow-through matter more than a low per-acre number.
        </p>
        <a
          href="/case-studies"
          className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-900 mt-4 cursor-pointer transition-colors group"
        >
          See why cheap applications often cost more in the end
          <i className="ri-arrow-right-line text-xs transition-transform group-hover:translate-x-1"></i>
        </a>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <a
          href="tel:+17179131780"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-800 text-white font-bold px-8 py-4 rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-phone-line"></i>
          Call to Confirm Quote
        </a>
        <a
          href="/#contact"
          className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-emerald-800 text-emerald-800 font-bold px-8 py-4 rounded-lg hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-mail-line"></i>
          Request Field Visit
        </a>
        <button
          onClick={onStartOver}
          className="inline-flex items-center justify-center gap-2 border-2 border-gray-300 text-gray-700 font-bold px-8 py-4 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          Start Over
        </button>
      </div>
    </div>
  );
}
